export interface NaceCode {
  code: string;
  description: string;
  level: number;
  /** Section letter (A–U) the code lives under. Source of truth for the
   * tree picker's division→section mapping — WZ 2025 diverges from NACE
   * Rev.2 (e.g. division 68 moved L→M), so hardcoded ranges go stale. */
  level1?: string | null;
}

const SECTION_ORDER = 'ABCDEFGHIJKLMNOPQRSTU';

/**
 * German-aware case fold: lowercase + ä→ae, ö→oe, ü→ue, ß→ss.
 * Mirrors the SQL de_fold() function so client-side dropdown filtering
 * matches the backend's matching behaviour ("Duess" finds "Düsseldorf").
 */
export function deFold(s: string): string {
  return s
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss');
}

export function naceSortKey(item: NaceCode): string {
  // Section row (level 1, single letter): order by SECTION_ORDER index.
  if (item.level === 1 && /^[A-Z]$/i.test(item.code)) {
    const idx = SECTION_ORDER.indexOf(item.code.toUpperCase());
    return `${String(idx === -1 ? 99 : idx).padStart(2, '0')}_000`;
  }
  // Any deeper code: section comes from the row's level1 column (DB truth).
  const sectionIdx = item.level1
    ? SECTION_ORDER.indexOf(item.level1.toUpperCase())
    : -1;
  const sectionPart = String(sectionIdx === -1 ? 99 : sectionIdx).padStart(2, '0');
  // Pad the leading numeric run so "10" sorts after "9" within a section.
  const leadingNum = item.code.match(/^(\d+)/);
  const numPart = leadingNum ? leadingNum[1].padStart(3, '0') : '000';
  return `${sectionPart}_${numPart}_${item.code}`;
}

// CURRENT_YEAR backs the founded-between inputs' max attribute only; the
// age buckets come from personAgeRanges(), which derives its own.
export const CURRENT_YEAR = new Date().getFullYear();

/**
 * Company lifecycle buckets. Disjoint and exhaustive — every company is in
 * exactly one — so the counts sum to the size of the index and ticking all
 * five is the same as ticking none.
 *
 * `descriptionKey` feeds the ⓘ next to the group label: "in liquidation" vs
 * "dissolved" vs "insolvent" are three different legal situations that all
 * read as "not trading normally", and users cannot be expected to know which
 * is which.
 */
export const LEGAL_STATUS_OPTIONS = [
  { value: 'active', labelKey: 'legalStatusOptions.active', defaultLabel: 'Active' },
  {
    value: 'in_liquidation',
    labelKey: 'legalStatusOptions.inLiquidation',
    defaultLabel: 'In liquidation',
  },
  {
    value: 'insolvent',
    labelKey: 'legalStatusOptions.insolvent',
    defaultLabel: 'Insolvent',
  },
  {
    value: 'dissolved',
    labelKey: 'legalStatusOptions.dissolved',
    defaultLabel: 'Dissolved',
  },
  { value: 'deleted', labelKey: 'legalStatusOptions.deleted', defaultLabel: 'Deleted' },
] as const;

/**
 * HGB § 267 size classes.
 *
 * Unlike {@link LEGAL_STATUS_OPTIONS} these do **not** partition the register.
 * A company is classified only where what it was required to publish says so —
 * withholding the P&L is a relief only small companies may claim (§ 326 Abs. 1
 * HGB) — so a company that publishes its turnover, or that sits outside
 * §§ 264ff entirely (Swiss register, e.K., eGbR), has no class at all. Ticking
 * every box is therefore a real filter ("classified at all"), not a no-op, and
 * the counts do not sum to the result total.
 *
 * Three options, not the four statutory classes. Every inference runs
 * *downward* from a claimed relief, and no relief asserts largeness — a große
 * Kapitalgesellschaft is exactly the one that claims nothing — so `gross`
 * can never be assigned.
 */
export const COMPANY_SIZE_OPTIONS = [
  {
    value: 'kleinst',
    labelKey: 'companySizeOptions.kleinst',
    defaultLabel: 'Micro-entity',
  },
  { value: 'klein', labelKey: 'companySizeOptions.klein', defaultLabel: 'Small' },
  {
    value: 'mittelgross',
    labelKey: 'companySizeOptions.mittelgross',
    defaultLabel: 'Medium-sized',
  },
] as const;

/** English fallbacks for the size-class ⓘ. Thresholds are the ones in force
 *  for fiscal years from 2024 (Delegated Directive (EU) 2023/2775); a company
 *  qualifies by not exceeding at least two of the three. */
export const COMPANY_SIZE_HELP_EN: Record<string, string> = {
  kleinst:
    '§ 267a HGB — at most two of: €450k balance sheet, €900k turnover, 10 employees. Files a balance sheet only.',
  klein:
    '§ 267 (1) HGB — at most two of: €7.5m balance sheet, €15m turnover, 50 employees. Publishes no profit-and-loss statement.',
  mittelgross:
    '§ 267 (2) HGB — at most two of: €25m balance sheet, €50m turnover, 250 employees. May report gross profit instead of turnover.',
};

/** English fallbacks for the ⓘ tooltip, keyed by bucket. The i18n bundles
 *  carry the real copy; these keep the component readable on its own and
 *  render correctly if a key is ever missing. */
export const LEGAL_STATUS_HELP_EN: Record<string, string> = {
  active: 'Trading normally — no insolvency or dissolution on record.',
  in_liquidation:
    'Dissolved and being wound up, with a Liquidator in office. Covers both a voluntary shareholder resolution and dissolution caused by insolvency.',
  insolvent:
    'Insolvency proceedings are open, or preliminary measures (vorläufige Insolvenzverwaltung) are in force.',
  dissolved:
    'Dissolved with no Liquidator in office, or an insolvency proceeding that has since been lifted (Verfahren aufgehoben).',
  deleted:
    'Removed from the Handelsregister entirely. Takes precedence over the other statuses.',
};

/**
 * EU public-procurement roles, from TED notices.
 *
 * Unlike {@link LEGAL_STATUS_OPTIONS} these do **not** partition the register:
 * they overlap (a company can be both), and ~99% of companies are in neither.
 * Ticking both therefore means "appears in TED at all" and is a genuine
 * filter, not the no-op that ticking every legal status is.
 *
 * `buyer` is intrinsically the smaller of the two by an order of magnitude,
 * and that is not a data gap: most contracting authorities are municipalities
 * or Anstalten des öffentlichen Rechts and have no Handelsregister entry to
 * be matched to in the first place.
 */
export const TENDER_ROLE_OPTIONS = [
  {
    value: 'winner',
    labelKey: 'tenderRoleOptions.winner',
    defaultLabel: 'Won public contracts',
  },
  {
    value: 'buyer',
    labelKey: 'tenderRoleOptions.buyer',
    defaultLabel: 'Contracting authority',
  },
] as const;

/** English fallbacks for the ⓘ tooltip — see LEGAL_STATUS_HELP_EN. */
export const TENDER_ROLE_HELP_EN: Record<string, string> = {
  winner:
    'Was awarded at least one public contract published in the EU tender database (TED).',
  buyer:
    'Published at least one tender as the contracting authority. Rare among registered companies — most public buyers are authorities with no Handelsregister entry.',
};

/** External-registry memberships the backend can OR-merge across. The
 *  `value` is the literal string the backend expects; `label` is what users
 *  see in the dropdown. `bbh` is expanded to its full name per product spec. */
export const MEMBERSHIP_OPTIONS: { value: string; label: string }[] = [
  { value: 'Steuerberaterkammer', label: 'Steuerberaterkammer' },
  { value: 'Wirtschaftsprueferkammer', label: 'Wirtschaftsprüferkammer' },
  { value: 'bbh', label: 'Buchhalter und Bilanzbuchhalter Bundesverband' },
  { value: 'BaFin', label: 'BaFin' },
  { value: 'LEI', label: 'LEI' },
];

/** Register types. Stored as the literal value the backend expects.
 *  Empty value = no filter (handled in collectFilters/URL writer). */
export const REGISTER_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'HRA', label: 'HRA — Handelsregister A' },
  { value: 'HRB', label: 'HRB — Handelsregister B' },
  { value: 'PR', label: 'PR — Partnerschaftsregister' },
  { value: 'VR', label: 'VR — Vereinsregister' },
  { value: 'GnR', label: 'GnR — Genossenschaftsregister' },
  { value: 'GsR', label: 'GsR — Gesellschaftsregister' },
  { value: 'CH-HR', label: 'CH-HR — Handelsregister (Schweiz)' },
  { value: 'AT-FB', label: 'FN — Firmenbuch (Österreich)' },
];
